import React from "react";
import { Building2, Globe2, Layers, FileCheck2, Workflow, ShieldCheck } from "lucide-react";
import { useSEO } from "@/lib/seo";
import CTASection from "@/components/CTASection";

const FOCUS = [
  { icon: FileCheck2, t: "Environmental engineering", d: "Technical depth in environmental regulations and reporting." },
  { icon: ShieldCheck, t: "Compliance", d: "Structured preparation aligned to EPCRA and related programs." },
  { icon: Layers, t: "Data management", d: "Clean, organized chemical and facility datasets." },
  { icon: FileCheck2, t: "Technical documentation", d: "Audit-ready records, registers and reporting packages." },
  { icon: Workflow, t: "Digital workflows", d: "Technology-enabled, repeatable delivery processes." },
  { icon: Globe2, t: "International delivery", d: "A specialized global team supporting U.S. facilities remotely." },
];

export default function About() {
  useSEO({
    title: "About Wasserlab",
    description:
      "Wasserlab is an India-based environmental consulting company providing remote environmental, sustainability and compliance support to U.S. industrial facilities.",
    path: "/about",
  });

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">About Wasserlab</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            A specialized global environmental compliance partner
          </h1>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <p className="text-lg leading-relaxed text-slate-700">
            Wasserlab is an environmental consulting company based in India providing environmental, sustainability and
            compliance support to businesses. We are expanding our remote technical delivery model to support U.S.
            industrial facilities.
          </p>
          <p className="mt-5 text-lg leading-relaxed text-slate-700">
            Our approach is transparent and professional: <span className="font-semibold text-navy-900">U.S. Environmental
            Compliance Support — Delivered by a Specialized Global Team.</span> We provide the technical preparation that
            reduces the workload on your internal EHS staff, while your organization retains final review and
            decision-making.
          </p>

          <h2 className="mt-14 font-heading text-2xl font-bold text-navy-900">What we focus on</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {FOCUS.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex items-start gap-4 rounded-xl border border-slate-200 p-5" data-testid={`about-focus-${i}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-navy-900">{f.t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{f.d}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-14 rounded-xl border border-slate-200 bg-slate-50 p-8">
            <div className="flex items-start gap-4">
              <Building2 className="mt-1 h-6 w-6 shrink-0 text-navy-900" aria-hidden="true" />
              <div>
                <h3 className="font-heading text-lg font-bold text-navy-900">How we operate</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Wasserlab provides remote environmental compliance support to U.S. clients through its international
                  delivery team. We do not maintain a physical U.S. office or U.S. employees, and we do not hold
                  government approvals or U.S. licenses. We are a technical and compliance preparation partner — not a
                  regulatory authority.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CTASection location="about-footer" />
    </>
  );
}
